import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, getToken, isMfaChallenge, setToken, type Me } from '../api/client';

/** Result of the first login step: done, or a second factor is required. */
export type LoginStep = { mfaRequired: false } | { mfaRequired: true; challenge: string };

interface AuthContextValue {
  me: Me | null;
  loading: boolean;
  login: (tenantSlug: string, email: string, password: string) => Promise<LoginStep>;
  verifyMfa: (challenge: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    void (async () => {
      if (getToken()) {
        try {
          const profile = await api.me();
          if (active) setMe(profile);
        } catch {
          setToken(null);
        }
      }
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  const login = async (tenantSlug: string, email: string, password: string): Promise<LoginStep> => {
    const result = await api.login(tenantSlug, email, password);
    if (isMfaChallenge(result)) return { mfaRequired: true, challenge: result.challenge };
    setToken(result.token);
    setMe(await api.me());
    return { mfaRequired: false };
  };

  const verifyMfa = async (challenge: string, code: string): Promise<void> => {
    const result = await api.verifyMfaLogin(challenge, code);
    setToken(result.token);
    setMe(await api.me());
  };

  const logout = async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Best-effort; clear local state regardless.
    }
    setToken(null);
    setMe(null);
  };

  const can = (permission: string): boolean =>
    me ? me.isPlatformAdmin || me.permissions.includes(permission) : false;

  return (
    <AuthContext.Provider value={{ me, loading, login, verifyMfa, logout, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
